import React, { useRef } from "react";
import { Headphones, DollarSign, ShieldCheck, MapPinned } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap } from "@/lib/motion";

const ICONS = [Headphones, DollarSign, ShieldCheck, MapPinned];

/* The warehouse opens like a roller door as you arrive (clip-path), the photo
   settling from a slight zoom; a stamped seal sits on it like a packing label. */
export default function WhyUsSection() {
  const { t, lang } = useLang();
  const root = useRef(null);

  useGsap(
    () => {
      gsap.fromTo(
        ".why-frame",
        { clipPath: "inset(0% 0% 100% 0% round 28px)" },
        {
          clipPath: "inset(0% 0% 0% 0% round 28px)",
          ease: "none",
          scrollTrigger: { trigger: ".why-frame", start: "top 90%", end: "top 35%", scrub: true },
        },
      );
      gsap.fromTo(
        ".why-photo",
        { scale: 1.25 },
        { scale: 1, ease: "none", scrollTrigger: { trigger: ".why-frame", start: "top bottom", end: "bottom top", scrub: true } },
      );
      gsap.from(".why-stamp", {
        scale: 1.6,
        opacity: 0,
        rotate: -24,
        duration: 0.7,
        ease: "power4.out",
        scrollTrigger: { trigger: ".why-frame", start: "top 40%", once: true },
      });
      gsap.from(".why-item", {
        y: 24,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        scrollTrigger: { trigger: ".why-list", start: "top 82%", once: true },
      });
    },
    [lang],
    root,
  );

  return (
    <section ref={root} data-testid="why-section" className="bg-[var(--bg)] text-[var(--ink)] py-28 lg:py-40 overflow-x-clip">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 relative">
          <div className="why-frame relative overflow-hidden rounded-[28px] aspect-[4/3] lg:aspect-[5/4] bg-[var(--forest-900)]">
            <picture>
              <source type="image/webp" media="(max-width: 767px)" srcSet="/images/loading-dock-sm.webp" />
              <source type="image/webp" srcSet="/images/loading-dock.webp" />
              <img
                src="/images/loading-dock.jpg"
                alt={t.media.dockAlt}
                width="1400"
                height="1120"
                loading="lazy"
                decoding="async"
                className="why-photo w-full h-full object-cover [filter:saturate(0.8)]"
              />
            </picture>
            <div className="absolute inset-0 bg-[oklch(0.45_0.09_138/0.25)] mix-blend-multiply" />
            <div className="absolute inset-0 bg-[linear-gradient(200deg,transparent_40%,oklch(0.17_0.028_140/0.65))]" />
          </div>
          <div
            className="why-stamp absolute -bottom-6 end-6 sm:end-10 grid place-items-center w-32 h-32 sm:w-36 sm:h-36 rounded-full border-[3px] border-[var(--signal)] bg-[var(--forest-950)] text-center text-[var(--signal)] rotate-[-10deg] shadow-2xl"
            aria-hidden
          >
            <span className="px-4 text-[13px] sm:text-sm font-black leading-tight uppercase tracking-wide">{t.hero.trustSafe}</span>
          </div>
        </div>

        <div className="lg:col-span-6">
          <p className="text-[15px] font-semibold text-[var(--accent-ink)]">{t.why.eyebrow}</p>
          <h2 className="type-display mt-4 text-[clamp(2rem,3.8vw,3.4rem)] max-w-[16ch]">{t.why.title}</h2>

          <ul className="why-list mt-12 grid sm:grid-cols-2 gap-x-10">
            {t.why.items.map((it, i) => {
              const Icon = ICONS[i] || Headphones;
              return (
                <li key={it.t} className="why-item flex gap-4 py-6 border-t border-[var(--line)]" data-testid={`why-item-${i}`}>
                  <Icon className="w-6 h-6 shrink-0 mt-0.5 text-[var(--accent-ink)]" aria-hidden />
                  <div>
                    <h3 className="font-extrabold text-lg">{it.t}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-[var(--muted-ink)]">{it.d}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
